import React, { useState } from 'react';
import Dropdown from '@splunk/react-ui/Dropdown';
import Menu from '@splunk/react-ui/Menu';
import SlidingPanels from '@splunk/react-ui/SlidingPanels';
import Button from '@splunk/react-ui/Button';
import DotsThreeVertical from '@splunk/react-icons/DotsThreeVertical';
import ArrowsRoundedRectangularDouble from '@splunk/react-icons/ArrowsRoundedRectangularDouble';
import styled from 'styled-components';
import ChevronLeft from '@splunk/react-icons/ChevronLeft';
import Tooltip from '@splunk/react-ui/Tooltip';
import { AnyTheme, VariableInterpolation, variables } from '@splunk/themes';
import { ngRoute } from '../../../../app/routing/ngRoute';
import { PropertyFilter, setPresetsInSearchParams } from './utils';
import Badge from '@splunk/react-ui/Badge';

const { Divider, Heading, Item } = Menu;

const StyledPanelContainer = styled.div`
    width: 270px;
`;

const StyledDeleteLabel = styled.span<{ disabled?: boolean }>`
    color: ${(props): VariableInterpolation<AnyTheme, 'accentColorNegative'> | undefined =>
        props.disabled ? undefined : variables.accentColorNegative};
`;

const StyledBackwardsContainer = styled.div`
    display: flex;
    align-items: center;
`;

const SwitchExperienceContainer = styled.div`
    align-items: center;
    display: flex;
    gap: 4px;
`;

const MIRROR_DELETE_TOOLTIP =
    'Change the permissions of this dashboard to inherit from a different group to remove this mirror';

const DASHBOARD_ACTIONS = 'dashboardActions';
const MORE_DASHBOARD_ACTIONS = 'moreDashboardActions';
const DASHBOARD_GROUP_ACTIONS = 'dashboardGroupActions';

export type DashboardNavHeaderActionsProps = {
    actions: {
        addDashboardToGroup: () => void;
        allowBuiltinMirrors: boolean;
        canBeMirrored: boolean;
        canEditGroup: boolean;
        canMakeEditable: boolean;
        canMakeReadOnly: boolean;
        canRemoveMirrors: boolean;
        cloneDashboard: () => void;
        dashboardViewsEnabled: boolean;
        deleteDashboard: () => void;
        enableCustomDashboardAsHomePage: boolean;
        groupIsServiceDiscovery: boolean;
        export: () => void;
        hasCharts: boolean;
        hasCreateDashboardGroupCapability: boolean;
        hasCreateShareableSnapshotCapability: boolean;
        isDeleteDisabled: boolean;
        isFullscreen: boolean;
        isMirror: boolean;
        isSnapshot: boolean;
        isWorkspace: boolean;
        makeDashboardEditable: () => void;
        makeDashboardReadOnly: () => void;
        mirrorsDisableReasonTooltipText: string;
        removeDashboardFromGroup: () => void;
        saveTempDashboard: () => void;
        setAsHomepage: () => void;
        share: () => void;
        showActionMenu: boolean;
        showDashboardCrossLinks: () => void;
        showDashboardInfo: () => void;
        showEditPermissionsModal: () => void;
        showEditVariablesModal: () => void;
        showEventOverlayModal: () => void;
        showRenameModal: () => void;
        showPermissionsLink: boolean;
        viewFullscreen: () => void;
    };
    groupActions: {
        deleteDashboardGroup: () => void;
        deleteDashboardGroupEnabled: boolean;
        exportEnabled: () => boolean;
        exportGroup: () => void;
        isTeamsLinksDisabled: boolean;
        showPageInfo: () => void;
        showPagePermissions: () => boolean;
        showPagePermissionsLink: boolean;
        showDeleteDashboardGroup: boolean;
        showTeamsLinksModal: () => void;
    };
    groupEnabled?: boolean;
    hasDeleteDashboardGroupCapability: boolean;
    isExportEnabled: boolean;
    model: { filters: PropertyFilter; id: string };
    modernDashboardsEnabled?: boolean;
    onTrackClick: (value: string) => void;
};

export const DashboardNavHeaderActions: (props: DashboardNavHeaderActionsProps) => JSX.Element = ({
    actions: {
        addDashboardToGroup,
        allowBuiltinMirrors,
        canBeMirrored,
        canEditGroup,
        canMakeEditable,
        canMakeReadOnly,
        canRemoveMirrors,
        cloneDashboard,
        dashboardViewsEnabled,
        deleteDashboard,
        enableCustomDashboardAsHomePage,
        export: exportDashboard,
        groupIsServiceDiscovery,
        hasCharts,
        hasCreateDashboardGroupCapability,
        hasCreateShareableSnapshotCapability,
        isDeleteDisabled,
        isFullscreen,
        isMirror,
        isSnapshot,
        isWorkspace,
        makeDashboardEditable,
        makeDashboardReadOnly,
        mirrorsDisableReasonTooltipText,
        removeDashboardFromGroup,
        saveTempDashboard,
        setAsHomepage,
        share,
        showActionMenu,
        showDashboardCrossLinks,
        showDashboardInfo,
        showEditPermissionsModal,
        showEditVariablesModal,
        showEventOverlayModal,
        showRenameModal,
        showPermissionsLink,
        viewFullscreen,
    },
    groupActions: {
        deleteDashboardGroup,
        deleteDashboardGroupEnabled,
        exportGroup,
        isTeamsLinksDisabled,
        showDeleteDashboardGroup,
        showPageInfo,
        showPagePermissions,
        showPagePermissionsLink,
        showTeamsLinksModal,
    },
    groupEnabled,
    hasDeleteDashboardGroupCapability,
    isExportEnabled,
    model,
    modernDashboardsEnabled,
    onTrackClick,
}): React.JSX.Element => {
    const [transition, setTransition] = useState<'forward' | 'backward'>('forward');
    const [activePanelId, setActivePanelId] = useState(DASHBOARD_ACTIONS);

    const goForwardToMoreDashboardActions = (event: React.MouseEvent): void => {
        event.stopPropagation();
        setActivePanelId(MORE_DASHBOARD_ACTIONS);
        setTransition('forward');
    };

    const goForwardToDashboardGroup = (event: React.MouseEvent): void => {
        event.stopPropagation();
        setActivePanelId(DASHBOARD_GROUP_ACTIONS);
        setTransition('forward');
    };

    const goBackward = (event: React.MouseEvent): void => {
        event.stopPropagation();
        setActivePanelId(DASHBOARD_ACTIONS);
        setTransition('backward');
    };

    const Toggle = (
        <div>
            <Button appearance="subtle" icon={<DotsThreeVertical />} />
        </div>
    );

    const onRequestOpen = (): void => {
        setActivePanelId(DASHBOARD_ACTIONS);
    };

    const switchToNewExperience = (): void => {
        const existingSearchParams = new URLSearchParams(ngRoute.history?.location.search);
        const searchParams = new URLSearchParams();

        const density = Math.round(
            Number.parseFloat(existingSearchParams.get('density') ?? '1') * 60
        );
        if (density !== 60 && !Number.isNaN(density)) {
            searchParams.set('d', String(density));
        }

        const startTime = existingSearchParams.get('startTime');
        if (startTime !== null) {
            const endTime = existingSearchParams.get('endTime') ?? 'now';
            if (endTime.toLowerCase() === 'now') {
                searchParams.set('t', startTime);
            } else {
                searchParams.set('t', `${startTime}--${endTime}`);
            }
        }

        const sourcePresets: { property?: string; value?: string[] }[] =
            model.filters?.sources?.map((source) => ({
                property: source?.property,
                value: source?.value,
            })) ?? [];

        const variablePresets: { property?: string; value?: string[] }[] =
            model.filters?.variables?.map((variable) => ({
                property: variable?.property,
                value: variable?.value ?? undefined,
            })) ?? [];

        // "sf_metric:foo,foo2"
        const urlSources: { property?: string; value?: string[] }[] = Array.from(
            existingSearchParams.entries() ?? []
        )
            .map(([key, value]) => ({ key, value }))
            .filter(({ key }) => key === 'sources[]')
            .map(({ value: searchParamValue }) => {
                const [key, ...rest] = searchParamValue?.split(':');
                const values = rest.join(':').split(',');

                return {
                    property: key,
                    value: values,
                };
            });

        // "Account ID=aws_account_id:["1", "2"]"
        const urlVariables: { property?: string; value?: string[] }[] = Array.from(
            existingSearchParams.entries() ?? []
        )
            .map(([key, value]) => ({ key, value }))
            .filter(({ key }) => key === 'variables[]')
            .map(({ value: searchParamValue }) => {
                const [key, ...rest] = searchParamValue?.split('=').slice(1).join('=').split(':');

                let values: string[] = [];

                try {
                    const parsedValues: string[] = JSON.parse(rest.join(':'));
                    if (Array.isArray(parsedValues)) {
                        values = parsedValues;
                    }
                } catch {}

                return {
                    property: key,
                    value: values,
                };
            });

        const finalSources = urlSources?.length > 0 ? urlSources : sourcePresets;
        const finalVariables = urlVariables?.length > 0 ? urlVariables : variablePresets;

        setPresetsInSearchParams(searchParams, finalSources.concat(finalVariables));

        ngRoute.history?.push({
            pathname: `/modern-dashboards/_/${model.id}`,
            search: searchParams.toString(),
        });
    };

    return (
        <Dropdown onRequestOpen={onRequestOpen} toggle={Toggle}>
            <StyledPanelContainer>
                <SlidingPanels activePanelId={activePanelId} transition={transition}>
                    <SlidingPanels.Panel key={DASHBOARD_ACTIONS} panelId={DASHBOARD_ACTIONS}>
                        <Menu focusMode="roving">
                            <Heading>Dashboard actions</Heading>
                            {hasCreateDashboardGroupCapability && (
                                <Item
                                    disabled={!hasCharts}
                                    onClick={(): void => {
                                        hasCharts &&
                                            (isSnapshot ? saveTempDashboard() : cloneDashboard());
                                        onTrackClick('dashboard-save-as');
                                    }}
                                >
                                    Save as
                                </Item>
                            )}
                            {dashboardViewsEnabled && hasCreateDashboardGroupCapability && (
                                <Item
                                    disabled={
                                        isWorkspace ||
                                        (groupIsServiceDiscovery && !allowBuiltinMirrors) ||
                                        !canBeMirrored
                                    }
                                    onClick={(): void => {
                                        !isWorkspace &&
                                            (!groupIsServiceDiscovery ||
                                                (groupIsServiceDiscovery && allowBuiltinMirrors)) &&
                                            canBeMirrored &&
                                            addDashboardToGroup();
                                        onTrackClick('dashboard-views-add-to-group-open-modal');
                                    }}
                                >
                                    <Tooltip
                                        content={mirrorsDisableReasonTooltipText}
                                        openDelay={'secondary'}
                                    >
                                        Add a mirror
                                    </Tooltip>
                                </Item>
                            )}
                            {hasCreateShareableSnapshotCapability && (
                                <Item
                                    disabled={!hasCharts}
                                    onClick={(): void => {
                                        hasCharts && share();
                                        onTrackClick('dashboard-dashboard-share');
                                    }}
                                >
                                    Share
                                </Item>
                            )}
                            {isExportEnabled && (
                                <Item
                                    onClick={(): void => {
                                        exportDashboard();
                                        onTrackClick('dashboard-export');
                                    }}
                                >
                                    Export
                                </Item>
                            )}
                            {showActionMenu && (
                                <Item
                                    onClick={(): void => {
                                        showRenameModal();
                                        onTrackClick('dashboard-rename');
                                    }}
                                >
                                    Rename
                                </Item>
                            )}
                            {!isFullscreen && (
                                <Item
                                    onClick={(): void => {
                                        viewFullscreen();
                                        onTrackClick('dashboard-enter-fullscreen');
                                    }}
                                >
                                    View fullscreen
                                </Item>
                            )}
                            {isMirror && (
                                <Item
                                    disabled={!canEditGroup || !canRemoveMirrors}
                                    onClick={(): void => {
                                        canEditGroup &&
                                            canRemoveMirrors &&
                                            removeDashboardFromGroup();
                                        onTrackClick(
                                            'dashboard-views-remove-from-group-open-modal'
                                        );
                                    }}
                                >
                                    <Tooltip
                                        content={
                                            !canRemoveMirrors ? MIRROR_DELETE_TOOLTIP : undefined
                                        }
                                    >
                                        <StyledDeleteLabel
                                            disabled={!canEditGroup || !canRemoveMirrors}
                                        >
                                            Remove this mirror
                                        </StyledDeleteLabel>
                                    </Tooltip>
                                </Item>
                            )}
                            {!isMirror && hasDeleteDashboardGroupCapability && (
                                <Item
                                    disabled={isDeleteDisabled}
                                    onClick={(): void => {
                                        !isDeleteDisabled && deleteDashboard();
                                        onTrackClick('dashboard-delete');
                                    }}
                                >
                                    <StyledDeleteLabel disabled={isDeleteDisabled}>
                                        Delete
                                    </StyledDeleteLabel>
                                </Item>
                            )}
                            <Divider />
                            <Item hasSubmenu onClick={goForwardToMoreDashboardActions}>
                                More dashboard actions
                            </Item>

                            <Divider />
                            <Item
                                disabled={!groupEnabled}
                                hasSubmenu
                                onClick={goForwardToDashboardGroup}
                            >
                                Dashboard group actions
                            </Item>
                            {modernDashboardsEnabled && (
                                <>
                                    <Divider />
                                    <Item onClick={switchToNewExperience}>
                                        <SwitchExperienceContainer>
                                            <ArrowsRoundedRectangularDouble />
                                            Switch to new experience
                                            <Badge
                                                backgroundColor={
                                                    variables.notificationColorInfoWeak
                                                }
                                                label="New"
                                            />
                                        </SwitchExperienceContainer>
                                    </Item>
                                </>
                            )}
                        </Menu>
                    </SlidingPanels.Panel>

                    <SlidingPanels.Panel
                        key={MORE_DASHBOARD_ACTIONS}
                        panelId={MORE_DASHBOARD_ACTIONS}
                    >
                        <Menu>
                            <Item onClick={goBackward}>
                                <StyledBackwardsContainer>
                                    <ChevronLeft />
                                    Back
                                </StyledBackwardsContainer>
                            </Item>
                            <Heading>More dashboard actions</Heading>
                            {showActionMenu && (
                                <Item
                                    onClick={(): void => {
                                        showDashboardInfo();
                                        onTrackClick('dashboard-dashboard-info');
                                    }}
                                >
                                    Dashboard info
                                </Item>
                            )}
                            {showPermissionsLink && (
                                <Item
                                    onClick={(): void => {
                                        showEditPermissionsModal();
                                        onTrackClick('dashboard-permissions-open');
                                    }}
                                >
                                    Permissions
                                </Item>
                            )}
                            {canMakeReadOnly && (
                                <Item
                                    onClick={(): void => {
                                        makeDashboardReadOnly();
                                        onTrackClick('dashboard-make-read-only');
                                    }}
                                >
                                    Make read only
                                </Item>
                            )}
                            {canMakeEditable && (
                                <Item
                                    onClick={(): void => {
                                        makeDashboardEditable();
                                        onTrackClick('dashboard-allow-edits');
                                    }}
                                >
                                    Allow edits
                                </Item>
                            )}
                            {showActionMenu && (
                                <Item
                                    onClick={(): void => {
                                        showEditVariablesModal();
                                        onTrackClick('dashboard-variables-open');
                                    }}
                                >
                                    Dashboard variables
                                </Item>
                            )}
                            {showActionMenu && (
                                <Item
                                    onClick={(): void => {
                                        showEventOverlayModal();
                                        onTrackClick('dashboard-event-overlay-suggestions');
                                    }}
                                >
                                    Event overlay suggestions
                                </Item>
                            )}
                            {showActionMenu && (
                                <Item
                                    onClick={(): void => {
                                        showDashboardCrossLinks();
                                        onTrackClick('dashboard-event-overlay-suggestions');
                                    }}
                                >
                                    Data links
                                </Item>
                            )}
                            {enableCustomDashboardAsHomePage && (
                                <Item
                                    onClick={(): void => {
                                        setAsHomepage();
                                        onTrackClick('dashboard-set-homepage');
                                    }}
                                >
                                    Set as Home page
                                </Item>
                            )}
                        </Menu>
                    </SlidingPanels.Panel>

                    <SlidingPanels.Panel
                        key={DASHBOARD_GROUP_ACTIONS}
                        panelId={DASHBOARD_GROUP_ACTIONS}
                    >
                        <Menu>
                            <Item onClick={goBackward}>
                                <StyledBackwardsContainer>
                                    <ChevronLeft />
                                    Back
                                </StyledBackwardsContainer>
                            </Item>
                            <Divider />
                            <Heading>Dashboard group actions</Heading>
                            <Item
                                onClick={(): void => {
                                    showPageInfo();
                                    onTrackClick('dashboard-group-info');
                                }}
                            >
                                Info
                            </Item>
                            {showPagePermissionsLink && (
                                <Item
                                    onClick={(): void => {
                                        showPagePermissions();
                                        onTrackClick('dashboard-group-permissions');
                                    }}
                                >
                                    Permissions
                                </Item>
                            )}
                            <Item
                                disabled={isTeamsLinksDisabled}
                                onClick={(): void => {
                                    showTeamsLinksModal();
                                    onTrackClick('dashboard-group-link-to-team');
                                }}
                            >
                                Links to teams
                            </Item>
                            {isExportEnabled && (
                                <Item
                                    onClick={(): void => {
                                        exportGroup();
                                        onTrackClick('dashboard-group-export');
                                    }}
                                >
                                    Export
                                </Item>
                            )}
                            {showDeleteDashboardGroup && hasDeleteDashboardGroupCapability && (
                                <Item
                                    disabled={!deleteDashboardGroupEnabled}
                                    onClick={(): void => {
                                        deleteDashboardGroup();
                                        onTrackClick('delete-dashboard-group');
                                    }}
                                >
                                    <StyledDeleteLabel disabled={!deleteDashboardGroupEnabled}>
                                        Delete
                                    </StyledDeleteLabel>
                                </Item>
                            )}
                        </Menu>
                    </SlidingPanels.Panel>
                </SlidingPanels>
            </StyledPanelContainer>
        </Dropdown>
    );
};
